'use client';

import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { NoScheduleReason, GroupedCourse } from '@/app/types/interfaces';
import { Button, IconButton } from './Button';
import { captureFocus, lockDocumentScroll, trapTabKey, unlockDocumentScroll } from './overlayBehavior';

interface Props {
  isOpen: boolean; onClose: () => void;
  noScheduleReasons: NoScheduleReason[] | null;
  selectedCourses: GroupedCourse[];
  onReviewCourse?: (groupId: string) => void;
  onReviewSelections?: () => void;
}
const titles: Record<NoScheduleReason['type'], string> = {
  all_sections_closed: 'No open sections',
  availability_excludes_course: 'Review time preferences',
  list_quantity_insufficient: 'Not enough eligible courses',
  pinned_conflict: 'Pinned sections overlap',
  pairwise_conflict: 'Courses overlap',
};

export default function NoScheduleModal({ isOpen, onClose, noScheduleReasons, selectedCourses, onReviewCourse, onReviewSelections }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!isOpen) return;
    lockDocumentScroll();
    const release = captureFocus(dialogRef.current);
    return () => { release(); unlockDocumentScroll(); };
  }, [isOpen]);
  if (!isOpen) return null;
  const review = (id: string) => { onClose(); onReviewCourse?.(id); };
  return createPortal(<div className="ss-overlay-backdrop fixed inset-0 z-[90] flex items-center justify-center" data-ss-overlay-kind="no-schedule" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-ss-overlay-material="transient" className="ss-overlay-panel relative flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-feature border shadow-e3" onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); onClose(); return; } trapTabKey(event, dialogRef.current); }}>
      <div className="ss-overlay-divider flex items-start justify-between gap-3 border-b p-4 sm:p-6"><div><h2 id={titleId} className="text-xl font-semibold text-content">No schedules possible</h2><p className="mt-1 text-sm text-content-muted">Review your course choices and allowed sections.</p></div><IconButton label="Close no schedule modal" variant="quiet" onClick={onClose}><XMarkIcon className="h-5 w-5" /></IconButton></div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6" data-ss-overlay-scroll-region>
        {noScheduleReasons?.length ? noScheduleReasons.map((reason, index) => {
          const affected = selectedCourses.filter(group =>
            reason.courseIds?.includes(group.id)
            || (reason.listId && group.isCourseList && group.courseList?.id === reason.listId)
            || group.sections.some(section => reason.involved?.includes(section.id)),
          );
          return <section key={`${reason.type}-${index}`} className="space-y-2 border-b border-line pb-4 last:border-0 last:pb-0"><h3 className="font-semibold text-content">{titles[reason.type]}</h3><p className="text-sm text-content-muted">{reason.message}</p>{onReviewCourse && affected.length > 0 && <div className="flex flex-wrap gap-2">{affected.map(group => <Button key={group.id} variant="secondary" onClick={() => review(group.id)}>Review {group.name}</Button>)}</div>}</section>;
        }) : <p className="text-sm text-content-muted">No specific issues were identified. Review the allowed sections and course-list quantities, or try fewer courses.</p>}
      </div>
      <div className="ss-overlay-divider flex flex-wrap justify-end gap-2 border-t p-4 sm:px-6">{onReviewSelections && <Button onClick={() => { onClose(); onReviewSelections(); }}>Review selections</Button>}<Button variant="secondary" onClick={onClose}>Close</Button></div>
    </div>
  </div>, document.body);
}
